/**
 * Shared character derivation — pure, framework-free.
 *
 * Computes the "view" object (attributes + derived stats) that downstream
 * renderers (Foundry sheet, Nuxt hero doc) display. This is the server-side
 * copy: the Cloud Function's /getCharacter endpoint calls into this and
 * ships the result alongside the raw character so Foundry can be a pure
 * renderer with no client-side derivation.
 *
 * Rules — mirror `composables/characterValidator.ts`:
 *   - NO Vue/Nuxt/Pinia imports.
 *   - NO imports from `models/*` — the models dep graph pulls in Firestore
 *     types and unrelated Corporation code we don't want dragged into the
 *     shared build. Minimal structural subtypes are declared locally.
 *   - Enum values (e.g. FeatureType.origin) are compared as string literals
 *     so the runtime doesn't need the FeatureType enum to exist.
 *
 * Source of truth: `components/CharacterHeroDoc.vue` — the Nuxt render
 * target. Formulas here must match what that file computes byte-for-byte
 * (for the fields this file supports). Do NOT "improve" the formulas.
 */

// ---------------------------------------------------------------------
// Local minimal types — structurally compatible with models/*.ts.
// ---------------------------------------------------------------------

/** Minimal shape of FeatureOption used by the derivation. */
interface DerivationFeatureOption {
	id: string;
	name: string;
	type: string;
	qty?: number;
}

/** Minimal shape of LevelDetail used by the derivation. */
interface DerivationLevelDetail {
	features: DerivationFeatureOption[];
}

/** Minimal shape of Character used by the derivation. */
interface DerivationCharacter {
	rank: number;
	details: DerivationLevelDetail[];
}

/** Minimal shape of a property Effect the derivation layer reads. */
interface DerivationEffect {
	type: string;
	value?: string;
	dice?: string;
	adjustment?: number;
}

/** Minimal shape of a Property (Firestore doc) used by the derivation. */
interface DerivationProperty {
	id: string;
	baseStats?: Record<string, number>;
	effects?: DerivationEffect[];
}

/** Property lookup function, injected by the caller (cache-backed). */
type GetPropertyById = (id: string) => DerivationProperty | undefined;

// ---------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------

/**
 * The 12 stats, in canonical display order. Mirrors
 * `components/CharacterHeroDoc.vue:336-340`.
 */
export const STAT_NAMES = [
	"agility",
	"fortitude",
	"might",
	"reflex",
	"intellect",
	"willpower",
	"tech",
	"persuasion",
	"perception",
	"allure",
	"foresight",
	"harmony",
] as const;

export type StatName = (typeof STAT_NAMES)[number];
export type AttributeMap = Record<StatName, number>;

export interface DerivedStats {
	hits: number;
	af: number;
	df: number;
	ef: number;
	speed: number;
	mass: number;
	grind: number;
	/**
	 * Maximum Wounds before Comatose / Dead, per `content/.../
	 * 4.appendix/2.conditions-and-effects.md`: `Fortitude + Willpower
	 * + Mass`. Wound COUNT (current tracker) is Foundry-side state and
	 * lives on the actor — this field is just the ceiling.
	 */
	maxWounds: number;
	/** Free Actions per turn. 1 per Tier: R0-3 = 1, R4-6 = 2, R7-10 = 3. */
	actions: number;
	/** Energy pool max. 1 per Tier: R0-3 = 1, R4-6 = 2, R7-10 = 3. */
	energy: number;
	/** Heroic Energy pool max = ceil(Rank / 2). */
	heroicEnergy: number;
}

/**
 * One modifier line in a derivation breakdown. Renders as
 * `{label}: {sign}{value}` in the Foundry tooltip. The total on the
 * sheet is the sum of all `value`s for that stat.
 */
export interface Breakdown {
	label: string;
	value: number;
}

/** Per-attribute breakdown — one entry per stat in `STAT_NAMES`. */
export type AttrsBreakdowns = Record<StatName, Breakdown[]>;

/** Per-derived-stat breakdown — keys match `DerivedStats`. */
export interface DerivedStatsBreakdowns {
	hits: Breakdown[];
	af: Breakdown[];
	df: Breakdown[];
	ef: Breakdown[];
	speed: Breakdown[];
	mass: Breakdown[];
	grind: Breakdown[];
	maxWounds: Breakdown[];
	actions: Breakdown[];
	energy: Breakdown[];
	heroicEnergy: Breakdown[];
}

/**
 * The three Cybercore saving throws. Each save is the raw sum of four
 * contributing attributes — no divisor, no rounding. Rolled as
 * `d100 + save value vs source TL`, so the save value stays in the same
 * numeric range as AF/EF (~10-30 at low ranks).
 *
 * Category → attributes:
 *   physical     = agility + fortitude + might + reflex
 *   mental       = intellect + willpower + tech + persuasion
 *   metaphysical = perception + allure + foresight + harmony
 *
 * Only the player rolls — saves are a passive defensive value.
 */
export type SaveName = "physical" | "mental" | "metaphysical";

export type SavesMap = Record<SaveName, number>;

export type SavesBreakdowns = Record<SaveName, Breakdown[]>;

/**
 * One row in the skills panel — matches the shape CharacterHeroDoc.vue's
 * `allSkills` computed produces. `booned` / `tieredBooned` are visibility
 * flags for the dice chips rendered next to the skill name; the actual
 * dice strings are in `BoonDice` (one per character, not per skill).
 */
export interface SkillEntry {
	id: string;
	name: string;
	total: number;
	rank: number;
	booned: boolean;
	tieredBooned: boolean;
}

/**
 * Auto-scaling boon dice display strings, derived from the character's
 * rank. Same string for every skill — renderers only decide whether to
 * show the chip per-skill via `SkillEntry.booned` / `.tieredBooned`.
 */
export interface BoonDice {
	ranked: string;
	tiered: string;
}

// ---------------------------------------------------------------------
// HITS per rank per core — mirrors CharacterHeroDoc.vue:376-381
// ---------------------------------------------------------------------

const HITS_PER_RANK: Record<string, number> = {
	"core-merc": 3,
	"core-commando": 3,
	"core-enforcer": 3,
	"core-heavy": 3,
	"core-saros": 3,
	"core-breacher": 2,
	"core-rigger": 2,
	"core-robonetic": 2,
	"core-commander": 1,
	"core-envoy": 1,
	"core-silhouette": 1,
	"core-medic": 1,
	"core-sci-ops": 1,
	"core-sec-ops": 1,
};

// ---------------------------------------------------------------------
// Character Tiers — the single source of truth for tier boundaries.
// Tier 1 = Ranks 0-3, Tier 2 = Ranks 4-6, Tier 3 = Ranks 7-10
// (see content/.../5.cores/1.cores-overview.md). Rank 0 counts as Tier 1.
// Foundry can't import this module, so foundary_vtt/module/combat/
// action-spend.mjs:tierForRank carries a hand-kept copy.
// ---------------------------------------------------------------------

export function tierForRank(rank: number): 1 | 2 | 3 {
	if (rank <= 3) return 1;
	if (rank <= 6) return 2;
	return 3;
}

/** Free Actions per turn: 1 per Tier. Mirrors action-spend.mjs. */
export function actionsForRank(rank: number): number {
	return tierForRank(rank);
}

/** Energy pool max: 1 per Tier baseline. Mirrors action-spend.mjs. */
export function energyMaxForRank(rank: number): number {
	return tierForRank(rank);
}

function tierLabel(rank: number): string {
	return `Tier ${tierForRank(rank)}`;
}

// ---------------------------------------------------------------------
// computeAttrs
// ---------------------------------------------------------------------

/**
 * Compute a character's 12 attributes.
 *
 * Base = origin property's baseStats (looked up via `getPropertyById`).
 * Plus the sum of every `stats`-typed feature's `qty` (default 1) keyed by
 * the stat name (case-insensitive).
 *
 * Mirrors `components/CharacterHeroDoc.vue:342-373`. Pure — no effect scan.
 */
export function computeAttrs(
	character: DerivationCharacter,
	getPropertyById: GetPropertyById,
): AttributeMap {
	const totals: AttributeMap = {
		agility: 0,
		fortitude: 0,
		might: 0,
		reflex: 0,
		intellect: 0,
		willpower: 0,
		tech: 0,
		persuasion: 0,
		perception: 0,
		allure: 0,
		foresight: 0,
		harmony: 0,
	};

	// Base stats from origin property (R0 only — mirrors the Nuxt code).
	const originFeature = character.details[0]?.features.find(
		(f) => f.type === "origin",
	);
	if (originFeature) {
		const originProp = getPropertyById(originFeature.id);
		if (originProp?.baseStats) {
			for (const s of STAT_NAMES) {
				totals[s] += originProp.baseStats[s] || 0;
			}
		}
	}

	// Purchased stat points across all ranks.
	for (const detail of character.details) {
		for (const f of detail?.features ?? []) {
			if (f.type === "stats") {
				// Stat feature name matches a stat name (case-insensitive).
				const key = f.name.toLowerCase() as StatName;
				if (key in totals) {
					totals[key] += f.qty || 1;
				}
			}
		}
	}

	return totals;
}

// ---------------------------------------------------------------------
// computeAttrsBreakdowns
// ---------------------------------------------------------------------

/**
 * Per-stat breakdown showing which features contribute to each attribute.
 * Mirrors `computeAttrs` but keeps the contribution source + value for
 * each line. Renders as a tooltip on the attribute row in the Foundry
 * sheet (and potentially elsewhere).
 *
 * Contributing sources:
 *   1. Origin property's `baseStats[stat]` — one line per non-zero stat.
 *   2. `stats`-typed features purchased per rank — one line per feature.
 */
export function computeAttrsBreakdowns(
	character: DerivationCharacter,
	getPropertyById: GetPropertyById,
): AttrsBreakdowns {
	const out: AttrsBreakdowns = {
		agility: [],
		fortitude: [],
		might: [],
		reflex: [],
		intellect: [],
		willpower: [],
		tech: [],
		persuasion: [],
		perception: [],
		allure: [],
		foresight: [],
		harmony: [],
	};

	// Origin contribution (R0 only, matching computeAttrs).
	const originFeature = character.details[0]?.features.find(
		(f) => f.type === "origin",
	);
	if (originFeature) {
		const originProp = getPropertyById(originFeature.id);
		if (originProp?.baseStats) {
			for (const s of STAT_NAMES) {
				const val = originProp.baseStats[s] || 0;
				if (val !== 0) {
					out[s].push({
						label: `Origin: ${originFeature.name || "Unknown"}`,
						value: val,
					});
				}
			}
		}
	}

	// Per-rank stat purchases.
	for (let r = 0; r < character.details.length; r++) {
		for (const f of character.details[r]?.features ?? []) {
			if (f.type !== "stats") continue;
			const key = f.name.toLowerCase() as StatName;
			if (!(key in out)) continue;
			const val = f.qty || 1;
			out[key].push({
				label: `Rank ${r}: ${f.name}`,
				value: val,
			});
		}
	}

	return out;
}

// ---------------------------------------------------------------------
// computeDerivedStats
// ---------------------------------------------------------------------

/**
 * Compute a character's derived stats (HITS, AF, DF, EF, SPD, MASS, GRIND).
 *
 * Formulas — mirror `components/CharacterHeroDoc.vue:383-420` (derivedStats)
 * and `:440-460` (the async hits watcher, minus the effect scan):
 *
 *     mass    = overrideMass  ?? originProp.baseStats.mass  ?? 3
 *     speed   = overrideSpeed ?? originProp.baseStats.speed ?? 4
 *     af      = fort + mass + rank
 *     df      = rank * 11 + (agi + per - 4)   // armour dodge bonus is Foundry-only
 *     ef      = rank * 11 + (fore + ref)
 *     grind   = rank
 *     hits    = 8 + fort + mass + (rank * hitsPerRank)
 *                   // TODO: add bonusHP effect scan
 *
 * Override detection: scan rank 0 features for any whose property has its
 * own baseStats.mass / baseStats.speed (Mutant Mass uses this). If found,
 * override the origin defaults.
 *
 * Pure. No effect scan yet — basic hits only. Characters with bonusHP
 * feats will show a slightly low HITS value until the scan lands.
 */
export function computeDerivedStats(
	character: DerivationCharacter,
	attrs: AttributeMap,
	getPropertyById: GetPropertyById,
): DerivedStats {
	// --- Origin base stats (R0 only) ---------------------------------
	const originFeature = character.details[0]?.features.find(
		(f) => f.type === "origin",
	);
	const originProp = originFeature
		? getPropertyById(originFeature.id)
		: undefined;
	const base = originProp?.baseStats || {};

	// --- Override scan (R0 features with their own baseStats) --------
	// Mirrors CharacterHeroDoc.vue:392-400 — Mutant Mass picker uses this.
	let overrideMass: number | undefined;
	let overrideSpeed: number | undefined;
	for (const f of character.details[0]?.features ?? []) {
		const prop = getPropertyById(f.id);
		const pb = prop?.baseStats;
		if (!pb) continue;
		if (typeof pb.mass === "number") overrideMass = pb.mass;
		if (typeof pb.speed === "number") overrideSpeed = pb.speed;
	}

	const mass = overrideMass ?? base.mass ?? 3;
	const speed = overrideSpeed ?? base.speed ?? 4;
	const rank = character.rank || 0;

	const fort = attrs.fortitude || 0;
	const agi = attrs.agility || 0;
	const per = attrs.perception || 0;
	const fore = attrs.foresight || 0;
	const ref = attrs.reflex || 0;

	// --- HITS per rank — walk details in order, take first core -----
	// Mirrors CharacterHeroDoc.vue:450-457.
	let hitsPerRank = 0;
	for (const detail of character.details) {
		const coreFeature = detail?.features?.find((f) => f.type === "core");
		if (coreFeature) {
			hitsPerRank = HITS_PER_RANK[coreFeature.id] || 0;
			break;
		}
	}

	// --- HITS bonus from feats / cores (bonusHP effect scan) --------
	// Each feat/core's property can carry one or more `bonusHP` effects
	// whose `dice` field encodes a frequency (once / every-rank / tier /
	// custom interval). We walk every feature, resolve its property, and
	// sum adjustment × frequency multiplier. See `scanEffectBonusTotal`.
	const bonusHP = scanEffectBonusTotal(
		rank,
		character,
		"bonusHP",
		getPropertyById,
	);
	const hits = 8 + fort + mass + rank * hitsPerRank + bonusHP;

	// --- Max Wounds ---------------------------------------------------
	// Per content/.../4.appendix/2.conditions-and-effects.md: when the
	// character goes DOWN (all HITS used), they gain a Wound. At Max
	// Wounds they are Comatose. Above Max Wounds they are Dead.
	//     Max Wounds = Fortitude + Willpower + Mass
	const willpower = attrs.willpower || 0;
	const maxWounds = fort + willpower + mass;

	return {
		hits,
		af: fort + mass + rank,
		df: rank * 11 + (agi + per - 4),
		ef: rank * 11 + (fore + ref),
		speed,
		mass,
		grind: rank,
		maxWounds,
		actions: actionsForRank(rank),
		energy: energyMaxForRank(rank),
		heroicEnergy: Math.ceil(rank / 2),
	};
}

// ---------------------------------------------------------------------
// computeDerivedStatsBreakdowns
// ---------------------------------------------------------------------

/**
 * Per-stat breakdown of the seven derived stats. Mirrors the same
 * formulas as `computeDerivedStats` but records each modifier with a
 * label so a tooltip on the Foundry sheet can show HOW the number was
 * derived.
 *
 * The sum of a breakdown's `value` fields MUST equal the corresponding
 * DerivedStats field — if they drift, the tooltip will lie. Keep this
 * function in lock-step with `computeDerivedStats` when either changes.
 */
export function computeDerivedStatsBreakdowns(
	character: DerivationCharacter,
	attrs: AttributeMap,
	getPropertyById: GetPropertyById,
): DerivedStatsBreakdowns {
	const rank = character.rank || 0;
	const fort = attrs.fortitude || 0;
	const agi = attrs.agility || 0;
	const per = attrs.perception || 0;
	const fore = attrs.foresight || 0;
	const ref = attrs.reflex || 0;

	// --- Resolve mass/speed source (origin or override) --------------
	const originFeature = character.details[0]?.features.find(
		(f) => f.type === "origin",
	);
	const originProp = originFeature
		? getPropertyById(originFeature.id)
		: undefined;
	const base = originProp?.baseStats || {};

	// Mass/speed override scan — mirrors the math path in
	// `computeDerivedStats`. Last R0 feature with baseStats wins. We only
	// care whether SOME source contributed (so we can label it "Origin"
	// vs "Default"); the specific winning feature is irrelevant for the
	// label because all R0 contributions are conceptually part of the
	// origin configuration from the player's perspective — a Mutant
	// Mass 3 pick is just "choose your Mutant body plan", same category
	// as a Rodi inheriting mass 5 directly.
	let overrideMass: number | undefined;
	let overrideSpeed: number | undefined;
	for (const f of character.details[0]?.features ?? []) {
		const prop = getPropertyById(f.id);
		const pb = prop?.baseStats;
		if (!pb) continue;
		if (typeof pb.mass === "number") overrideMass = pb.mass;
		if (typeof pb.speed === "number") overrideSpeed = pb.speed;
	}

	const mass = overrideMass ?? base.mass ?? 3;
	const speed = overrideSpeed ?? base.speed ?? 4;

	// --- Core + hitsPerRank ------------------------------------------
	let hitsPerRank = 0;
	let coreFeatureName = "";
	for (const detail of character.details) {
		const coreFeature = detail?.features?.find((f) => f.type === "core");
		if (coreFeature) {
			hitsPerRank = HITS_PER_RANK[coreFeature.id] || 0;
			coreFeatureName = coreFeature.name || "Core";
			break;
		}
	}

	// --- BonusHP contributions with labels (per feature) -------------
	// We re-walk what `scanEffectBonusTotal` did, but record each
	// contribution with its feature name instead of summing silently.
	const bonusHPContributions: Breakdown[] = [];
	for (
		let r = 0;
		r <= rank && r < character.details.length;
		r++
	) {
		for (const feature of character.details[r]?.features ?? []) {
			const prop = getPropertyById(feature.id);
			if (!prop?.effects) continue;
			for (const effect of prop.effects) {
				if (effect.type !== "bonusHP") continue;
				const multiplier = getFrequencyMultiplier(
					effect.dice || "",
					r,
					rank,
				);
				const contribution = (effect.adjustment || 0) * multiplier;
				if (contribution === 0) continue;
				const label = multiplier > 1 ?
					`${feature.name || "Feature"} × ${multiplier}` :
					(feature.name || "Feature");
				bonusHPContributions.push({label, value: contribution});
			}
		}
	}

	// --- Build breakdowns --------------------------------------------
	// Mass / Speed — always labeled under the origin feature. R0 sub-
	// features with baseStats (Mutant Mass picker, etc.) are part of the
	// origin configuration from the player's perspective, so they share
	// the "Origin: {name}" label. The "Default" fallback only fires
	// when no feature with baseStats exists at all (no origin or origin
	// without baseStats → hits the formula defaults mass=3, speed=4).
	const originName = originFeature?.name || "Unknown";

	const massBreakdown: Breakdown[] = [];
	const massFromOrigin =
		overrideMass !== undefined || typeof base.mass === "number";
	if (massFromOrigin && originFeature) {
		massBreakdown.push({label: `Origin: ${originName}`, value: mass});
	} else {
		massBreakdown.push({label: "Default", value: mass});
	}

	const speedBreakdown: Breakdown[] = [];
	const speedFromOrigin =
		overrideSpeed !== undefined || typeof base.speed === "number";
	if (speedFromOrigin && originFeature) {
		speedBreakdown.push({label: `Origin: ${originName}`, value: speed});
	} else {
		speedBreakdown.push({label: "Default", value: speed});
	}

	// Grind = rank
	const grindBreakdown: Breakdown[] = [{label: "Rank", value: rank}];

	// AF = fort + mass + rank
	const afBreakdown: Breakdown[] = [
		{label: "Fortitude", value: fort},
		{label: "Mass", value: mass},
	];
	if (rank !== 0) afBreakdown.push({label: "Rank", value: rank});

	// DF = rank * 11 + (agi + per - 4). Armour dodge bonus is Foundry-only.
	const dfCombined = agi + per - 4;
	const dfBreakdown: Breakdown[] = [
		{label: `Agility ${agi} + Perception ${per} − 4`, value: dfCombined},
	];
	if (rank !== 0) dfBreakdown.push({label: "Rank × 11", value: rank * 11});

	// EF = rank * 11 + (fore + ref)
	const efCombined = fore + ref;
	const efBreakdown: Breakdown[] = [
		{label: `Foresight ${fore} + Reflex ${ref}`, value: efCombined},
	];
	if (rank !== 0) efBreakdown.push({label: "Rank × 11", value: rank * 11});

	// HITS = 8 + fort + mass + rank * hitsPerRank + bonusHP
	const hitsBreakdown: Breakdown[] = [
		{label: "Base", value: 8},
		{label: "Fortitude", value: fort},
		{label: "Mass", value: mass},
	];
	if (rank !== 0 && hitsPerRank > 0) {
		hitsBreakdown.push({
			label: `${coreFeatureName} × Rank (${hitsPerRank} × ${rank})`,
			value: rank * hitsPerRank,
		});
	}
	for (const contrib of bonusHPContributions) {
		hitsBreakdown.push(contrib);
	}

	// Max Wounds = Fortitude + Willpower + Mass
	// (Conditions doc: at Max the character is Comatose; above Max, Dead.)
	const willpowerForWounds = attrs.willpower || 0;
	const maxWoundsBreakdown: Breakdown[] = [
		{label: "Fortitude", value: fort},
		{label: "Willpower", value: willpowerForWounds},
		{label: "Mass", value: mass},
	];

	// Actions / Energy / Heroic — tier-based, mirrors action-spend.mjs
	const tier = tierLabel(rank);
	const actionsBreakdown: Breakdown[] = [{label: tier, value: actionsForRank(rank)}];
	const energyBreakdown: Breakdown[] = [{label: tier, value: energyMaxForRank(rank)}];
	const heroicEnergyBreakdown: Breakdown[] = [{label: "⌈Rank ÷ 2⌉", value: Math.ceil(rank / 2)}];

	return {
		hits: hitsBreakdown,
		af: afBreakdown,
		df: dfBreakdown,
		ef: efBreakdown,
		speed: speedBreakdown,
		mass: massBreakdown,
		grind: grindBreakdown,
		maxWounds: maxWoundsBreakdown,
		actions: actionsBreakdown,
		energy: energyBreakdown,
		heroicEnergy: heroicEnergyBreakdown,
	};
}

// ---------------------------------------------------------------------
// computeSaves / computeSavesBreakdowns
// ---------------------------------------------------------------------

/**
 * Compute the three Cybercore saving throws from a pre-computed
 * attribute map. Pure — takes `AttributeMap` directly, no character
 * walking or property lookups. Save value = raw sum of the 4
 * contributing attributes per category, no divisor.
 *
 * Attribute groupings are hardcoded per save to document the formulas
 * in code. Do NOT derive by slicing `STAT_NAMES` — a future reorder of
 * that constant would silently corrupt saves.
 */
export function computeSaves(attrs: AttributeMap): SavesMap {
	return {
		physical:
			(attrs.agility || 0) +
			(attrs.fortitude || 0) +
			(attrs.might || 0) +
			(attrs.reflex || 0),
		mental:
			(attrs.intellect || 0) +
			(attrs.willpower || 0) +
			(attrs.tech || 0) +
			(attrs.persuasion || 0),
		metaphysical:
			(attrs.perception || 0) +
			(attrs.allure || 0) +
			(attrs.foresight || 0) +
			(attrs.harmony || 0),
	};
}

/**
 * Per-save breakdown: one line per contributing attribute, each with
 * `{ label: "Agility", value: attrs.agility }`. The existing
 * `formatBreakdown` helper in the Foundry sheet sums the line values
 * and appends a `Total: N` row — for pure sum formulas this works
 * without any special handling, so saves reuse it as-is.
 */
export function computeSavesBreakdowns(
	attrs: AttributeMap,
): SavesBreakdowns {
	return {
		physical: [
			{label: "Agility", value: attrs.agility || 0},
			{label: "Fortitude", value: attrs.fortitude || 0},
			{label: "Might", value: attrs.might || 0},
			{label: "Reflex", value: attrs.reflex || 0},
		],
		mental: [
			{label: "Intellect", value: attrs.intellect || 0},
			{label: "Willpower", value: attrs.willpower || 0},
			{label: "Tech", value: attrs.tech || 0},
			{label: "Persuasion", value: attrs.persuasion || 0},
		],
		metaphysical: [
			{label: "Perception", value: attrs.perception || 0},
			{label: "Allure", value: attrs.allure || 0},
			{label: "Foresight", value: attrs.foresight || 0},
			{label: "Harmony", value: attrs.harmony || 0},
		],
	};
}

// ---------------------------------------------------------------------
// Boon dice helpers — pure functions. This is the canonical
// framework-free version; `composables/useEffectScanner.ts` has a legacy
// copy with a latent rank-0-returns-empty bug. Foundry / server / Nuxt
// should all converge on THIS implementation.
// ---------------------------------------------------------------------

/**
 * Ranked boon dice chain — auto-scales per rank.
 *
 * R0 and R1 both start at 1d4 so rank-0 characters see their boon dice
 * during character creation. From R2 onward the chain advances one step
 * per rank. Beyond R6 a second die is appended to the base d20.
 *
 *   R0 → 1d4      R4 → 1d10     R7 → 1d20+1d4    R10 → 1d20+1d10
 *   R1 → 1d4      R5 → 1d12     R8 → 1d20+1d6
 *   R2 → 1d6      R6 → 1d20     R9 → 1d20+1d8
 *   R3 → 1d8
 *
 * The game caps characters at rank 10, so ranks above that shouldn't
 * happen in practice — anything past R10 falls to the 1d20+1d12 cap.
 */
const DICE_CHAIN = ["d4", "d6", "d8", "d10", "d12", "d20"];

function getRankedBoonDice(rank: number): string {
	if (rank < 0) return "";
	// R0 and R1 share the first step so rank-0 characters see their
	// boon at character creation.
	const idx = Math.max(0, rank - 1);
	if (idx < DICE_CHAIN.length) return `1${DICE_CHAIN[idx]}`;
	const overflow = idx - DICE_CHAIN.length;
	if (overflow < DICE_CHAIN.length - 1) {
		return `1d20+1${DICE_CHAIN[overflow]}`;
	}
	return "1d20+1d12"; // cap
}

/**
 * Tiered boon dice — auto-scales per tier.
 * T1(R0-3) = d4, T2(R4-6) = d6, T3(R7-10) = d8.
 * Rank 0 is in T1 so it gets d4 (same as the ranked boon at R0/R1).
 */
function getTieredBoonDice(rank: number): string {
	if (rank < 0) return "";
	const tier = tierForRank(rank);
	if (tier === 1) return "1d4";
	if (tier === 2) return "1d6";
	return "1d8";
}

// ---------------------------------------------------------------------
// Effect scanning — port of useEffectScanner.ts:scanEffectBonuses, but
// synchronous. Property effects are pre-resolved into the server-side
// cache (see CachedProperty in functions/src/characters-api.ts), so
// there's no async loadEffects step. Nuxt's version has to await the
// Pinia property store; Foundry's version reads already-joined data.
// ---------------------------------------------------------------------

/**
 * Count how many tier boundaries are crossed going from `from` to `to`
 * (inclusive of the starting tier). Used by the "tier"-frequency
 * multiplier below. Mirrors the Nuxt helper exactly.
 */
function getTiersSpanned(from: number, to: number): number {
	let count = 1;
	for (let r = from + 1; r <= to; r++) {
		if (tierForRank(r) !== tierForRank(r - 1)) count++;
	}
	return count;
}

/**
 * Resolve the multiplier applied to a bonus effect based on its `dice`
 * field, which doubles as a frequency marker for non-dice bonuses.
 *
 *   ""       / "once" → 1          (one-time bonus at the rank it was taken)
 *   "1"               → currentRank - rankTaken + 1   (every rank since)
 *   "2" / "3" / ...   → floor((curr - taken) / interval) + 1
 *   "rank"            → currentRank                   (scales with current rank)
 *   "tier"            → tiers spanned from taken → current
 *
 * Mirrors `composables/useEffectScanner.ts:getFrequencyMultiplier`.
 */
function getFrequencyMultiplier(
	dice: string,
	rankTaken: number,
	currentRank: number,
): number {
	if (!dice || dice === "once") return 1;
	if (dice === "1") return currentRank - rankTaken + 1;
	if (dice === "rank") return currentRank;
	if (dice === "tier") return getTiersSpanned(rankTaken, currentRank);
	const interval = parseInt(dice);
	if (!isNaN(interval) && interval > 1) {
		return Math.floor((currentRank - rankTaken) / interval) + 1;
	}
	return 1;
}

/**
 * Sum every `effect.adjustment × frequency multiplier` for effects of
 * the given type attached to the character's features' properties, up
 * to and including the character's current rank.
 *
 * Used by `computeDerivedStats` for the bonusHP scan that feeds into
 * HITS. Future expansions (bonusSP, bonusAP, bonusStat, bonusWound)
 * call this same function with a different `effectType`.
 *
 * Pure — `getPropertyById` is the only injected dependency, and the
 * returned property is expected to have pre-resolved `effects[]` (the
 * server-side cache joins property.effectRefs → effects at load time).
 */
function scanEffectBonusTotal(
	currentRank: number,
	character: DerivationCharacter,
	effectType: string,
	getPropertyById: GetPropertyById,
	filter?: { value?: string },
): number {
	let total = 0;
	for (
		let r = 0;
		r <= currentRank && r < character.details.length;
		r++
	) {
		for (const feature of character.details[r]?.features ?? []) {
			const prop = getPropertyById(feature.id);
			if (!prop?.effects) continue;
			for (const effect of prop.effects) {
				if (effect.type !== effectType) continue;
				if (
					filter?.value !== undefined &&
					effect.value !== filter.value
				) {
					continue;
				}
				const multiplier = getFrequencyMultiplier(
					effect.dice || "",
					r,
					currentRank,
				);
				total += (effect.adjustment || 0) * multiplier;
			}
		}
	}
	return total;
}

/**
 * Compute the two boon-dice display strings for a character's current rank.
 * One pair per character, not per skill — the renderer decides which skills
 * get the chip via `SkillEntry.booned` / `.tieredBooned`.
 */
export function computeBoonDice(character: DerivationCharacter): BoonDice {
	const rank = character.rank || 0;
	return {
		ranked: getRankedBoonDice(rank),
		tiered: getTieredBoonDice(rank),
	};
}

// ---------------------------------------------------------------------
// computeSkills
// ---------------------------------------------------------------------

/**
 * Resolve which skill ids are booned by this character.
 *
 * A skill is *booned* when any `skillBoon`-type feature on the character
 * targets it. The link goes: skillBoon feature → property lookup → find
 * the property's `boon`-type effect → the effect's `value` is the target
 * skill id. This is symmetric with how tieredBoon resolves through knacks.
 *
 * (CharacterHeroDoc.vue has a latent bug here — it uses the skillBoon
 * feature's own id instead of resolving through the property effect. We
 * do the correct thing in the shared composable.)
 */
function computeBoonedSkillIds(
	character: DerivationCharacter,
	getPropertyById: GetPropertyById,
): Set<string> {
	const ids = new Set<string>();
	for (const detail of character.details) {
		for (const f of detail?.features ?? []) {
			if (f.type !== "skillBoon") continue;
			const prop = getPropertyById(f.id);
			if (!prop) continue;
			const boonEffect = prop.effects?.find((e) => e.type === "boon");
			if (boonEffect?.value) ids.add(boonEffect.value);
		}
	}
	return ids;
}

/**
 * Resolve which skill ids are tieredBooned by this character.
 *
 * Knack features → property lookup → `tieredBoon`-type effect → target
 * skill id. Mirrors CharacterHeroDoc.vue:492-506.
 */
function computeTieredBoonedSkillIds(
	character: DerivationCharacter,
	getPropertyById: GetPropertyById,
): Set<string> {
	const ids = new Set<string>();
	for (const detail of character.details) {
		for (const f of detail?.features ?? []) {
			if (f.type !== "knack") continue;
			const prop = getPropertyById(f.id);
			if (!prop) continue;
			const tbEffect = prop.effects?.find((e) => e.type === "tieredBoon");
			if (tbEffect?.value) ids.add(tbEffect.value);
		}
	}
	return ids;
}

/**
 * Compute the skill rows for the character sheet. Sums `qty` across all
 * ranks for each unique skill id, attaches `booned` / `tieredBooned`
 * flags, and returns the list sorted alphabetically by name.
 *
 * Mirrors `components/CharacterHeroDoc.vue:508-530`. Pure — no mutation
 * of the input character.
 */
export function computeSkills(
	character: DerivationCharacter,
	getPropertyById: GetPropertyById,
): SkillEntry[] {
	const boonedIds = computeBoonedSkillIds(character, getPropertyById);
	const tieredBoonedIds = computeTieredBoonedSkillIds(
		character,
		getPropertyById,
	);

	const map = new Map<string, SkillEntry>();
	for (let r = 0; r < character.details.length; r++) {
		for (const f of character.details[r]?.features ?? []) {
			if (f.type !== "skill") continue;
			const existing = map.get(f.id);
			if (existing) {
				existing.total += f.qty || 1;
			} else {
				map.set(f.id, {
					id: f.id,
					name: f.name,
					total: f.qty || 1,
					rank: r,
					booned: boonedIds.has(f.id),
					tieredBooned: tieredBoonedIds.has(f.id),
				});
			}
		}
	}

	return [...map.values()].sort((a, b) => a.name.localeCompare(b.name));
}
